import { Eyebrow, formatUtcDate, StatusDot, token } from '@bp-files/ui';
import { STORAGE_COPY } from '@/components/Storage/storage.copy';
import { DECIMAL, formatBytes } from '@/lib/formatBytes';
import type { StorageUsageState } from '@/lib/useStorageUsage';
import type { StorageUsagePoint } from '@/lib/api';

/**
 * The daily usage series — **the evidence for a band review** (STO-7, PLN-5).
 *
 * PLN-5 adjusts a band from an agency's peak rolling-30-day transfer and its
 * peak storage over the term. Rendering the same pair here is what lets an
 * agency argue its band up or down from numbers it can already see, rather than
 * from an operator's spreadsheet it cannot.
 *
 * Four things about the drawing are decisions rather than styling:
 *
 * - **A gap is not a zero.** `storageBytes` is null on a day the rollup never
 *   sampled, and the storage line breaks there. Drawing it to the floor would
 *   claim the agency held nothing that day, which is a measurement nobody took
 *   (§11). Transfer has no such case: a day with no row genuinely moved no
 *   bytes.
 * - **Two charts, not one shared canvas.** Transfer is a flow measured per
 *   day and storage is a level; a reader asked to take both off one set of
 *   axes first has to work out which shape belongs to which scale. Each chart
 *   gets its own baseline, its own peak gridline, and its own x-axis dates —
 *   a reader unsure what a bar or a line meant had no scale to check it
 *   against before this, only a peak figure in a legend below.
 * - **Every chart states its own scale.** A dashed gridline at the tallest
 *   thing it draws, labelled with that day's or that sample's value, plus a
 *   few dates along the bottom. Not a full tick ladder — STO-7's chart is
 *   supplementary evidence, not a spreadsheet — but enough that "how tall is
 *   tall" and "which day is this" both have an answer on the chart itself.
 * - **Inline SVG, no dependency.** `packages/ui` ships no chart and the design
 *   system defines none; a charting library is an ADR of its own (Article 3).
 *   Colours come through `token()` because raw hex is a lint error here.
 *
 * A failure renders as a missing chart and nothing else. This is supplementary
 * to the allowance figures an admin came for — `FolderView`'s rule about its
 * `useFolders` call, one surface over.
 */

const WIDTH = 720;
const HEIGHT = 130;
/** Space below the baseline reserved for date ticks. */
const AXIS_HEIGHT = 20;
/** Space above the plot area reserved for the peak gridline and its label. */
const TOP_PADDING = 18;
const BASELINE = HEIGHT - AXIS_HEIGHT;
const PLOT_HEIGHT = BASELINE - TOP_PADDING;

function bytes(value: number): string {
  return formatBytes(value, STORAGE_COPY.units, DECIMAL);
}

/** Month and day only — the range above the chart already states the year. */
function formatShortDay(iso: string): string {
  return new Date(iso).toLocaleDateString(STORAGE_COPY.dateLocale, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Up to five indices, evenly spaced from the first point to the last —
 * enough to anchor "which day is this" without a tick per day, which would
 * be unreadable text over a year-long term.
 */
function tickIndices(length: number): number[] {
  if (length <= 1) return [0];

  const count = Math.min(length, 5);
  const indices = new Set<number>();
  for (let k = 0; k < count; k += 1) {
    indices.add(Math.round((k * (length - 1)) / (count - 1)));
  }
  return [...indices].sort((a, b) => a - b);
}

export function UsageSeries({ state }: { state: StorageUsageState }) {
  if (state.kind === 'loading') {
    return (
      <p role="status" className="text-xs" style={{ color: token('bp-text-muted') }}>
        {STORAGE_COPY.states.loading}
      </p>
    );
  }

  if (state.kind === 'error') {
    // A `status`, never an `alert`: the figures above are intact and nothing an
    // admin needs has failed. The chart is evidence for a conversation that
    // happens once a year.
    return (
      <p role="status" className="text-xs" style={{ color: token('bp-text-muted') }}>
        {STORAGE_COPY.series.unavailable}
      </p>
    );
  }

  const { series, peaks, since, until } = state.usage;

  return (
    <section className="flex flex-col gap-2">
      {/*
        The range sits beside the heading rather than under the charts, because
        it is the first thing that makes this section readable: the transfer
        figures above are counted to their own as-of stamp, and a chart whose
        period is not stated next to its name reads as though it should cover
        the same span. It does not — this is the series the rollup has actually
        sampled, which on a young agency is far shorter than the term.
      */}
      <div className="flex flex-wrap items-baseline gap-3">
        <Eyebrow as="h2">{STORAGE_COPY.series.heading}</Eyebrow>
        <span className="text-xs text-muted">
          {STORAGE_COPY.series.range(
            formatUtcDate(since, STORAGE_COPY.dateLocale),
            formatUtcDate(until, STORAGE_COPY.dateLocale),
          )}
        </span>
      </div>
      <p className="max-w-prose text-xs text-muted">{STORAGE_COPY.series.note}</p>

      {series.length === 0 ? (
        <p className="text-xs text-muted">{STORAGE_COPY.series.empty}</p>
      ) : (
        <div className="flex flex-col gap-5">
          <TransferChart points={series} since={since} until={until} />
          <StorageChart points={series} since={since} until={until} />
        </div>
      )}

      {/*
        The peak 30-day and term figures — PLN-5's evidence, distinct from
        each chart's own "peak day drawn" gridline above. A dot's colour here
        must match what each chart actually draws with the same token, or the
        legend lies about its own chart; the storage line's token
        (`bp-blue-rail`) is not one of `StatusDot`'s fixed tones, so this uses
        the same tokens the chart uses rather than the nearest-looking one.
      */}
      <ul className="flex flex-wrap gap-4 text-xs text-muted">
        <li className="flex items-center gap-2">
          <StatusDot tone="blue" />
          <span>{STORAGE_COPY.series.transfer}</span>
          <span>{STORAGE_COPY.series.peaks.transfer(bytes(peaks.rolling30dTransferBytes))}</span>
        </li>
        <li className="flex items-center gap-2">
          <StatusDot tone="amber" />
          <span>{STORAGE_COPY.series.recipientTransfer}</span>
        </li>
        <li className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="inline-block size-2 shrink-0 rounded-full"
            style={{ background: token('bp-blue-rail') }}
          />
          <span>{STORAGE_COPY.series.storage}</span>
          <span>
            {peaks.storageBytes === null
              ? STORAGE_COPY.series.peaks.storageUnknown
              : STORAGE_COPY.series.peaks.storage(bytes(peaks.storageBytes))}
          </span>
        </li>
      </ul>
    </section>
  );
}

/** The dashed gridline at a chart's tallest drawn value, and its label. */
function PeakGridline({ label }: { label: string }) {
  return (
    <g>
      <line
        x1={0}
        y1={TOP_PADDING}
        x2={WIDTH}
        y2={TOP_PADDING}
        stroke={token('bp-border')}
        strokeWidth={1}
        strokeDasharray="4 3"
      />
      <text
        x={4}
        y={TOP_PADDING - 5}
        fill={token('bp-text-muted')}
        fontSize={10}
        textAnchor="start"
      >
        {label}
      </text>
    </g>
  );
}

/** The "0" mark at the baseline — the floor every bar and the line share. */
function ZeroLabel() {
  return (
    <text x={4} y={BASELINE - 4} fill={token('bp-text-muted')} fontSize={10} textAnchor="start">
      {STORAGE_COPY.series.axis.zero}
    </text>
  );
}

/** A handful of date labels along the bottom, anchored so none run off the edge. */
function DateTicks({ points, step }: { points: StorageUsagePoint[]; step: number }) {
  const ticks = tickIndices(points.length);

  return (
    <>
      {ticks.map((index) => {
        const point = points[index];
        if (!point) return null;

        const x = index * step + step / 2;
        const anchor = index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle';

        return (
          <text
            key={point.date}
            x={x}
            y={HEIGHT - 6}
            fill={token('bp-text-muted')}
            fontSize={10}
            textAnchor={anchor}
          >
            {formatShortDay(point.date)}
          </text>
        );
      })}
    </>
  );
}

/**
 * Daily transfer — a stacked bar per day, split into the two attributions
 * (PLN-8): what this agency read, and what plan-less readers read out of it.
 */
function TransferChart({
  points,
  since,
  until,
}: {
  points: StorageUsagePoint[];
  since: string;
  until: string;
}) {
  const step = WIDTH / Math.max(points.length, 1);
  const peak = Math.max(
    1,
    ...points.map((point) => point.transferBytes + point.recipientTransferBytes),
  );
  const caption = STORAGE_COPY.series.charts.transfer.caption(
    formatUtcDate(since, STORAGE_COPY.dateLocale),
    formatUtcDate(until, STORAGE_COPY.dateLocale),
  );

  const bars = points.map((point, index) => {
    const total = point.transferBytes + point.recipientTransferBytes;
    const height = (total / peak) * PLOT_HEIGHT;
    const ownShare = total === 0 ? 0 : point.transferBytes / total;

    return {
      x: index * step,
      width: Math.max(step - 2, 1),
      own: height * ownShare,
      other: height * (1 - ownShare),
      y: BASELINE - height,
    };
  });

  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-medium text-body">{STORAGE_COPY.series.charts.transfer.heading}</p>
      <svg
        role="img"
        aria-label={caption}
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        className="w-full"
        preserveAspectRatio="none"
      >
        <title>{caption}</title>

        <PeakGridline label={STORAGE_COPY.series.axis.peakDay(bytes(peak))} />
        <ZeroLabel />

        <line
          x1={0}
          y1={BASELINE}
          x2={WIDTH}
          y2={BASELINE}
          stroke={token('bp-border')}
          strokeWidth={1}
        />

        {bars.map((bar, index) => (
          <g key={points[index]?.date ?? String(index)}>
            <rect
              x={bar.x}
              y={bar.y}
              width={bar.width}
              height={bar.other}
              fill={token('bp-amber')}
              opacity={0.7}
            />
            <rect
              x={bar.x}
              y={bar.y + bar.other}
              width={bar.width}
              height={bar.own}
              fill={token('bp-blue')}
            />
          </g>
        ))}

        <DateTicks points={points} step={step} />
      </svg>
    </div>
  );
}

/**
 * Stored — a line broken at every unsampled day (see the module doc). Its
 * own chart, its own scale: a quiet transfer week must not read as an empty
 * account, which is what one shared scale with the bars above would risk.
 */
function StorageChart({
  points,
  since,
  until,
}: {
  points: StorageUsagePoint[];
  since: string;
  until: string;
}) {
  const step = WIDTH / Math.max(points.length, 1);
  const sampled = points.filter((point) => point.storageBytes !== null);
  const peak = Math.max(1, ...sampled.map((point) => point.storageBytes ?? 0));
  const caption = STORAGE_COPY.series.charts.storage.caption(
    formatUtcDate(since, STORAGE_COPY.dateLocale),
    formatUtcDate(until, STORAGE_COPY.dateLocale),
  );

  // Broken into runs at every unsampled day. One polyline across a gap would
  // interpolate a level nobody measured.
  const runs: string[] = [];
  let run: string[] = [];
  points.forEach((point, index) => {
    if (point.storageBytes === null) {
      if (run.length > 0) runs.push(run.join(' '));
      run = [];
      return;
    }
    const x = index * step + step / 2;
    const y = BASELINE - (point.storageBytes / peak) * PLOT_HEIGHT;
    run.push(`${String(Math.round(x))},${String(Math.round(y))}`);
  });
  if (run.length > 0) runs.push(run.join(' '));

  // A leading gap is the common real case — days before the rollup existed.
  // Naming the last unmeasured day is what makes the line's late start read
  // as "not sampled yet" rather than as a rendering error.
  const firstSampledIndex = points.findIndex((point) => point.storageBytes !== null);
  const leadingGapThrough = firstSampledIndex > 0 ? points[firstSampledIndex - 1]?.date : undefined;

  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-medium text-body">{STORAGE_COPY.series.charts.storage.heading}</p>
      <svg
        role="img"
        aria-label={caption}
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        className="w-full"
        preserveAspectRatio="none"
      >
        <title>{caption}</title>

        {sampled.length > 0 ? (
          <PeakGridline label={STORAGE_COPY.series.axis.peak(bytes(peak))} />
        ) : null}
        <ZeroLabel />

        <line
          x1={0}
          y1={BASELINE}
          x2={WIDTH}
          y2={BASELINE}
          stroke={token('bp-border')}
          strokeWidth={1}
        />

        {runs.map((run) => (
          <polyline
            key={run}
            points={run}
            fill="none"
            stroke={token('bp-blue-rail')}
            strokeWidth={1.5}
          />
        ))}

        <DateTicks points={points} step={step} />
      </svg>
      {leadingGapThrough ? (
        <p className="text-xs text-muted">
          {STORAGE_COPY.series.gapNote(formatShortDay(leadingGapThrough))}
        </p>
      ) : null}
    </div>
  );
}
